import {
  Box,
  Button,
  Flex,
  FlexProps,
  HStack,
  IconButton,
  IconButtonProps,
  Menu,
  Portal,
  Text
} from "@chakra-ui/react"
import type { IconName } from "@pathwright/pathicons"
import Link, { LinkProps } from "@pathwright/ux/src/components/link/Link"
import { PathiconProps } from "@pathwright/ux/src/components/pathicon/ExpensivePathicon"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import RevealOnGroupHover from "@pathwright/ux/src/components/style/RevealOnGroupHover"
import classNames from "classnames"
import { motion } from "framer-motion"
import { Indicator } from "../button/IconButton"

export type OptionsMenuItemProps = {
  icon?: IconName
  iconProps?: Partial<PathiconProps>
  label?: string
  closeOnClick?: boolean
  indicator?: boolean
} & LinkProps

export const OptionsMenuItem = ({
  icon,
  iconProps,
  label,
  onClick,
  closeOnClick,
  children,
  indicator,
  ...props
}: OptionsMenuItemProps) => {
  return (
    <Menu.Item asChild closeOnSelect={closeOnClick} value={label as string}>
      <Link
        onClick={onClick}
        w="100%"
        display="block"
        borderRadius="none"
        py={1.5}
        px={2}
        color="gray.500"
        aria-label={label}
        cursor="pointer"
        variant="plain"
        _hover={{ bg: "blackAlpha.200", textDecor: "none" }}
        {...props}
      >
        {children || (
          <HStack align="center" justify="flex-start" w="100%">
            {!!icon && <Pathicon icon={icon} {...iconProps} />}
            {!!label && (
              <Text
                fontSize="sm"
                my={0}
                fontWeight="normal"
                position="relative"
              >
                {label}
                {indicator && <Indicator top="0.2em" right="-0.5em" />}
              </Text>
            )}
          </HStack>
        )}
      </Link>
    </Menu.Item>
  )
}

export type OptionsMenuProps = {
  children?: React.ReactNode | null
  options?: OptionsMenuItemProps[]
  selectedOptionLabel?: string
  // Conditionally render Popover.Content in a portal.
  renderPortal?: boolean
  triggerProps?: IconButtonProps
  menuProps?: Partial<Menu.RootProps>
  renderOnGroupHover?: boolean
  isLoading?: boolean
} & Omit<FlexProps, "children">

const OptionsMenu = ({
  children,
  options,
  selectedOptionLabel,
  renderPortal,
  triggerProps,
  menuProps,
  className,
  renderOnGroupHover,
  isLoading,
  ...props
}: OptionsMenuProps) => {
  // Store Menu.List for conditionally rendering in portal.
  const content = (
    <Menu.Positioner>
      <Menu.Content
        w="fit-content"
        zIndex="modal"
        border="solid 1px"
        borderColor="gray.100"
        borderRadius="4px"
        outline="none"
        boxShadow="none"
        overflow="hidden"
        p={0}
        minW={0}
        display={isLoading ? "none" : "block"}
      >
        {children}
        {options?.map((item, index) => (
          <OptionsMenuItem
            key={index}
            icon={item.icon}
            label={item.label}
            onClick={item.onClick}
          />
        ))}
      </Menu.Content>
    </Menu.Positioner>
  )

  // Conditionally revealing on group hover.
  const TriggerRenderingComponent = renderOnGroupHover
    ? RevealOnGroupHover
    : Box

  return (
    <Flex asChild className={classNames("OptionMenu", className)} {...props}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{
          opacity: 1,
          transition: { ease: "easeInOut", duration: 0.3, delay: 0.1 }
        }}
      >
        <Menu.Root
          positioning={{ placement: "right-start" }}
          // Defer rendering Menu.Content until opened.
          // There seems to be a glitch with Menu that causes
          // it to render in the wrong place before it's been initially
          // opened, which can cause, in this case, the Menu to
          // add white space to right of UI.
          // TODO: chakra ui v3 upgrade: this lazy approach may be unnecessary now?
          lazyMount
          unmountOnExit
          {...menuProps}
        >
          <Menu.Context>
            {(menu: { open: boolean }) => (
              <TriggerRenderingComponent
                // Lets parents (e.g. library Course card) reveal the trigger while open
                // without relying on :focus-within, which stays true after close.
                data-options-menu-open={menu.open ? "" : undefined}
              >
                {selectedOptionLabel ? (
                  <Menu.Trigger asChild>
                    <Button
                      size="sm"
                      variant="subtle"
                      loading={isLoading}
                      {...triggerProps}
                    >
                      {selectedOptionLabel}
                      <Pathicon icon="chevron-down" />
                    </Button>
                  </Menu.Trigger>
                ) : (
                  <Menu.Trigger asChild>
                    <IconButton
                      aria-label="Options Menu"
                      size="xs"
                      variant="subtle"
                      loading={isLoading}
                      {...triggerProps}
                    >
                      <Pathicon icon="ellipses-horizontal" />
                    </IconButton>
                  </Menu.Trigger>
                )}
              </TriggerRenderingComponent>
            )}
          </Menu.Context>
          {renderPortal ? <Portal>{content}</Portal> : content}
        </Menu.Root>
      </motion.div>
    </Flex>
  )
}

export default OptionsMenu
